import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { normalizeVietnamese } from '../../utils/normalizeVietnamese';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

interface Product {
  id: string | number;
  name: string;
  price: number;
  reference?: string;
  imageId?: string;
}

interface ModernProductSelectorProps {
  onAddProduct: (product: Product) => void;
  onReduceProduct?: (productId: string | number) => void; // Callback to reduce quantity
  onClose?: () => void;
  currentItems?: Array<{ productId: string | number; quantity: number }>;
}

const PAGE_SIZE = 12;

export const ModernProductSelector: React.FC<ModernProductSelectorProps> = ({
  onAddProduct,
  onReduceProduct,
  onClose,
  currentItems = [],
}) => {
  const { translate } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          perPage: PAGE_SIZE.toString(),
          sort: 'name',
          order: 'ASC',
        });

        if (debouncedSearch) {
          params.append('q', normalizeVietnamese(debouncedSearch));
        }

        console.log(
          '🛍️ Product selector fetching products from /api/products...'
        );
        const response = await authenticatedFetch(`/api/products?${params}`);

        if (response.ok) {
          const data = await response.json();
          console.log(
            `✅ Product selector loaded ${
              Array.isArray(data) ? data.length : data.data?.length || 0
            } products`
          );

          // Handle both formats: direct array or wrapped in data property
          let loadedProducts: Product[] = [];
          if (Array.isArray(data)) {
            loadedProducts = data;
            setTotal(data.length);
          } else {
            loadedProducts = data.data || [];
            setTotal(data.total || 0);
          }

          // Extra client-side filtering so that searching with a representative
          // word (e.g. "trà") only shows products whose names include that word.
          if (debouncedSearch) {
            const normalizedSearch = normalizeVietnamese(debouncedSearch)
              .toLowerCase()
              .trim();

            loadedProducts = loadedProducts.filter((product) =>
              normalizeVietnamese(product.name)
                .toLowerCase()
                .includes(normalizedSearch)
            );
          }

          setProducts(loadedProducts);
        } else {
          console.error(
            `❌ Product selector fetch failed:`,
            response.status,
            response.statusText
          );
        }
      } catch (error) {
        console.error('❌ Error fetching products in selector:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [page, debouncedSearch]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  // Check if there are more products
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < (total || 0));
  }, [page, total]);

  const getProductQuantity = (productId: string | number) => {
    const item = currentItems.find((item) => item.productId === productId);
    return item?.quantity || 0;
  };

  const handleAddProduct = (product: Product) => {
    onAddProduct(product);
  };

  const loadMore = () => {
    if (hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">
              {translate('pos.orders.fields.searchProducts')}
            </CardTitle>
            {onClose && (
              <Button variant="outline" size="sm" onClick={onClose}>
                {translate('pos.common.action.close')}
              </Button>
            )}
          </div>
          <div className="relative">
            <Input
              type="text"
              placeholder={translate('pos.orders.fields.searchProducts')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-10"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="max-h-[60vh] overflow-y-auto p-4">
            {isLoading && page === 1 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {translate('pos.orders.messages.noProductsFound')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => {
                  const currentQuantity = getProductQuantity(product.id);
                  return (
                    <Card
                      key={product.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleAddProduct(product)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm truncate">
                              {product.name}
                            </h3>
                            {product.reference && (
                              <p className="text-xs text-gray-500 truncate">
                                {product.reference}
                              </p>
                            )}
                          </div>
                          <Badge variant="secondary" className="ml-2">
                            {formatCurrency(product.price)}
                          </Badge>
                        </div>

                        {currentQuantity > 0 && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t">
                            <span className="text-xs text-gray-600">
                              {translate('pos.orders.fields.quantity')}:{' '}
                              {currentQuantity}
                            </span>
                            <div className="flex items-center gap-1">
                              {onReduceProduct && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 w-6 p-0"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onReduceProduct(product.id);
                                  }}
                                >
                                  -
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 w-6 p-0"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleAddProduct(product);
                                }}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {hasMore && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                  ) : null}
                  {translate('pos.common.action.load_more')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
